import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AttachedDocument } from '../contract/attached-document.contract';
import { DocumentLink } from '../contract/document-link.contract';
import { FileLikeObject } from '../contract/file-like-object.contract';
import { ImageLink } from '../contract/image-link.contract';

export class FileUtils {
  public static readonly allowedImageMimeTypes: string[] = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
  ];

  public static readonly globalDirectories: string[] = [
    'equipmenttype',
  ];

  public static isPicture(file: ImageLink | DocumentLink | File | AttachedDocument): boolean {
    const mimeType = this.getMimeType(file);
    return this.allowedImageMimeTypes.indexOf(mimeType) !== -1;
  }

  public static isFromGlobalDirectory(imageKey: string): boolean {
    return Boolean(imageKey && FileUtils.globalDirectories.some(dir => imageKey.startsWith(dir)));
  }

  private static getMimeType(file: ImageLink | DocumentLink | File | AttachedDocument): string {
    if (this.isImageLink(file)) {
      return file.mimeType;
    } else if (this.isDocumentLink(file) || this.isAttachedDocument(file)) {
      return file.fileType;
    } else if (this.isFileLikeObject(file) || this.isFile(file)) {
      return file.type;
    }
    return '';
  }

  public static splitDocumentLinks(docs: DocumentLink[]): { documents: DocumentLink[], images: DocumentLink[] } {
    const documents: DocumentLink[] = [];
    const images: DocumentLink[] = [];
    (docs || []).forEach(doc => (FileUtils.isPicture(doc) ? images : documents).push(doc));
    return { documents, images };
  }

  public static downloadFile(response: HttpResponse<Blob | MediaSource>, openInNewTab?: boolean, fileName?: string): void {
    const contentDispositionHeader = response.headers.get('Content-Disposition');
    if (!contentDispositionHeader) {
      return;
    }

    const url: string = window.URL.createObjectURL(<Blob | MediaSource>response.body);
    const link: HTMLAnchorElement = document.createElement('a');
    const fileNameProperty: string = contentDispositionHeader.split(';')[2];
    const filenameString: string = fileNameProperty.split('=')[1];
    const filename: string = fileName == null ? filenameString.split('"')[1] : fileName;

    if (openInNewTab) {
      window.open(url);
    }

    link.href = url;
    link.download = filename;
    link.dispatchEvent(new MouseEvent(`click`, { bubbles: true, cancelable: true, view: window }));
    window.URL.revokeObjectURL(url);
    link.remove();
  }

  public static downloadDocument(fileUrl: string, fileName: string): void {
    new Observable((observer) => {
      const xhr = new XMLHttpRequest();
      xhr.open('get', fileUrl, true);
      xhr.responseType = 'blob';
      xhr.onload = function () {
        if (xhr.readyState === 4) {
          observer.next(xhr.response);
          observer.complete();
        }
      };
      xhr.send();
    }).subscribe((blob: unknown) => {
      const url: string = window.URL.createObjectURL(<Blob | MediaSource>blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.dispatchEvent(new MouseEvent(`click`, { bubbles: true, cancelable: true, view: window }));
      window.URL.revokeObjectURL(url);
      link.remove();
    });
  }

  public static isDocumentLink(file: unknown): file is DocumentLink {
    return Boolean(
      (<DocumentLink>file).fileType &&
      (<DocumentLink>file).fileExists
    );
  }

  public static filterAndSortImages(documentLinks: DocumentLink[], thumbnailKey?: string): DocumentLink[] {
    if (!documentLinks) {
      return documentLinks;
    }

    documentLinks = documentLinks.filter(doc => doc?.documentKey && FileUtils.isPicture(doc));
    documentLinks.sort((a, b) => {
      const timeA = a?.uploadDate ? new Date(a.uploadDate).getTime() : 0;
      const timeB = b?.uploadDate ? new Date(b.uploadDate).getTime() : 0;
      return timeA - timeB;
    });
    const thumbnailIndex = documentLinks.findIndex(image => image?.documentKey === thumbnailKey);
    if (thumbnailIndex > 0) {
      documentLinks.unshift(documentLinks.splice(thumbnailIndex, 1)[0]);
    }
    return documentLinks;
  }

  private static isImageLink(file: unknown): file is ImageLink {
    return Boolean((<ImageLink>file).mimeType);
  }

  private static isAttachedDocument(file: unknown): file is AttachedDocument {
    return Boolean((<AttachedDocument>file).fileType);
  }

  private static isFileLikeObject(file: unknown): file is FileLikeObject {
    return Boolean((<FileLikeObject>file).type) && Boolean((<FileLikeObject>file).rawFile);
  }

  private static isFile(file: unknown): file is File {
    return Boolean((<File>file).type);
  }

  public static isDocument(mimeType: string): boolean {
    return this.allowedImageMimeTypes.indexOf(mimeType) === -1;
  }

  public static encodeDocumentKey(documentKey: string): string {
    if (!documentKey) {
      return documentKey
    }

    const documentKeyParts = documentKey.split('/');
    return documentKeyParts.map(part => encodeURIComponent(part)).join('/');
  }

  public static handleBlob(blob: Blob, contentType: string): void {
    if (blob) {
      const fileBlob = new Blob([blob], { type: contentType });
      const fileURL = URL.createObjectURL(fileBlob);
      window.open(fileURL, '_blank');
    }
  }

  public static downloadBlob(fileBlob: Blob | null, filenamePrefix: string) {
    if (fileBlob) {
      const now = new Date();
      const formattedDate = `${now.getDate()}_${now.getMonth() + 1}_${now.getFullYear()}__${now.getHours()}_${now.getMinutes()}`;
      const filename = `${filenamePrefix}_${formattedDate}`;
      const url = URL.createObjectURL(fileBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      console.error('No file available to download.');
    }
  }

  public static getEntityIdFromDocumentKey(documentKey: string): string {
    const codeIndex = documentKey.indexOf('/') + 1;
    const code = documentKey.substring(codeIndex);
    return code.split('/')[0];
  }
}
